import { addMonthsIso, monthOf, type IsoMonth } from "../dates";
import type { CategoryId } from "../ids";
import type { Account, BudgetEntry, Category, OverspendMode, Transaction } from "../schemas";
import { budgetLines, uncategorisedKey, type BudgetLine, type BudgetRowKey } from "./lines";

export interface CategoryMonth {
  carryIn: number;
  budgeted: number;
  activity: number;
  available: number;
}

export interface MonthSummary {
  month: IsoMonth;
  /** raw[m−1] */
  notBudgetedLastMonth: number;
  /** overspent[m−1] */
  overspentLastMonth: number;
  /** income[m] */
  income: number;
  /** Σ budgeted[c,m] */
  budgeted: number;
  budgetedInFuture: number;
  /** ATB[m], the figure shown at the top of the month. Negative means overbudgeted. */
  availableToBudget: number;
  /** raw[m] */
  raw: number;
  /** overspent[m]: deducted from next month's ATB. */
  overspent: number;
  /** Σ activity over categories and Uncategorised. */
  activity: number;
  /** Σ available over categories and Uncategorised. */
  available: number;
}

export interface BudgetInput {
  accounts: Iterable<Account>;
  categories: Iterable<Category>;
  transactions: Iterable<Transaction>;
  budgetEntries: Iterable<BudgetEntry>;
}

const zeroCategoryMonth: CategoryMonth = { carryIn: 0, budgeted: 0, activity: 0, available: 0 };

export class BudgetResult {
  constructor(
    /** M0, or null when there is nothing in the budget yet. */
    readonly firstMonth: IsoMonth | null,
    readonly lastMonth: IsoMonth | null,
    private readonly months: Map<IsoMonth, MonthSummary>,
    private readonly cells: Map<BudgetRowKey, Map<IsoMonth, CategoryMonth>>,
    private readonly modes: Map<BudgetRowKey, OverspendMode>,
  ) {}

  month(month: IsoMonth): MonthSummary {
    const found = this.months.get(month);
    if (found) {
      return found;
    }
    if (this.lastMonth !== null && month > this.lastMonth) {
      // Beyond the computed range nothing happens, so the last month's figures roll forward.
      const last = this.month(this.lastMonth);
      const raw = last.raw - last.overspent;
      const isNext = month === addMonthsIso(this.lastMonth, 1);
      return {
        ...emptyMonth(month),
        notBudgetedLastMonth: isNext ? last.raw : raw,
        overspentLastMonth: isNext ? last.overspent : 0,
        raw,
        availableToBudget: raw,
        available: last.available + last.overspent,
      };
    }
    return emptyMonth(month);
  }

  cell(row: BudgetRowKey, month: IsoMonth): CategoryMonth {
    const cells = this.cells.get(row);
    const found = cells?.get(month);
    if (found) {
      return found;
    }
    if (cells && this.lastMonth !== null && month > this.lastMonth) {
      const last = cells.get(this.lastMonth) ?? zeroCategoryMonth;
      const carryIn = carryForward(last.available, this.modes.get(row) ?? "reset");
      return { carryIn, budgeted: 0, activity: 0, available: carryIn };
    }
    return zeroCategoryMonth;
  }

  uncategorised(month: IsoMonth): CategoryMonth {
    return this.cell(uncategorisedKey, month);
  }
}

function emptyMonth(month: IsoMonth): MonthSummary {
  return {
    month,
    notBudgetedLastMonth: 0,
    overspentLastMonth: 0,
    income: 0,
    budgeted: 0,
    budgetedInFuture: 0,
    availableToBudget: 0,
    raw: 0,
    overspent: 0,
    activity: 0,
    available: 0,
  };
}

function carryForward(available: number, mode: OverspendMode): number {
  return available >= 0 || mode === "carry" ? available : 0;
}

/**
 * The YNAB 4 budget engine (SPEC §5.2). Computes every month from M0 to the later of
 * `throughMonth` and the last month with data.
 */
export function computeBudget(input: BudgetInput, throughMonth?: IsoMonth): BudgetResult {
  const modes = new Map<BudgetRowKey, OverspendMode>([[uncategorisedKey, "reset"]]);
  for (const c of input.categories) {
    if (!c.deleted) {
      modes.set(c.id, c.overspend);
    }
  }
  const rowOf = (categoryId: CategoryId | undefined): BudgetRowKey =>
    categoryId !== undefined && modes.has(categoryId) ? categoryId : uncategorisedKey;

  const lines = budgetLines(input.accounts, input.transactions);
  const activity = new Map<BudgetRowKey, Map<IsoMonth, number>>();
  const budgeted = new Map<BudgetRowKey, Map<IsoMonth, number>>();
  const incomeByMonth = new Map<IsoMonth, number>();
  let first: IsoMonth | null = null;
  let last: IsoMonth | null = throughMonth ?? null;
  const see = (m: IsoMonth) => {
    if (m.length !== 7) {
      return;
    }
    if (first === null || m < first) {
      first = m;
    }
    if (last === null || m > last) {
      last = m;
    }
  };

  for (const l of lines) {
    const m = monthOf(l.date);
    see(m);
    addIncomeOrActivity(l, m);
  }
  for (const b of input.budgetEntries) {
    if (b.deleted || b.amount === 0 || !modes.has(b.categoryId)) {
      continue;
    }
    see(b.month);
    add(budgeted, b.categoryId, b.month, b.amount);
  }

  function addIncomeOrActivity(l: BudgetLine, m: IsoMonth): void {
    if (l.income === "thisMonth") {
      incomeByMonth.set(m, (incomeByMonth.get(m) ?? 0) + l.amount);
    } else if (l.income === "nextMonth") {
      const next = addMonthsIso(m, 1);
      see(next);
      incomeByMonth.set(next, (incomeByMonth.get(next) ?? 0) + l.amount);
    } else {
      add(activity, rowOf(l.categoryId), m, l.amount);
    }
  }

  const months = new Map<IsoMonth, MonthSummary>();
  const cells = new Map<BudgetRowKey, Map<IsoMonth, CategoryMonth>>();
  const firstMonth = first as IsoMonth | null;
  const lastMonth = firstMonth === null ? null : (last as IsoMonth | null);
  if (firstMonth === null || lastMonth === null) {
    return new BudgetResult(null, null, months, cells, modes);
  }

  const rows = Array.from(modes.keys());
  for (const r of rows) {
    cells.set(r, new Map());
  }
  const order: IsoMonth[] = [];
  const carry = new Map<BudgetRowKey, number>();
  let prevRaw = 0;
  let prevOverspent = 0;
  // After 9999-12 comes "10000-01", which sorts before it as a string: stop at four-digit years.
  for (let m = firstMonth; m <= lastMonth && m.length === 7; m = addMonthsIso(m, 1)) {
    order.push(m);
    let monthBudgeted = 0;
    let monthActivity = 0;
    let monthAvailable = 0;
    let overspent = 0;
    for (const r of rows) {
      const carryIn = carry.get(r) ?? 0;
      const b = budgeted.get(r)?.get(m) ?? 0;
      const a = activity.get(r)?.get(m) ?? 0;
      const available = carryIn + b + a;
      const mode = modes.get(r) ?? "reset";
      if (mode === "reset" && available < 0) {
        overspent -= available;
      }
      carry.set(r, carryForward(available, mode));
      monthBudgeted += b;
      monthActivity += a;
      monthAvailable += available;
      if (carryIn !== 0 || b !== 0 || a !== 0) {
        cells.get(r)?.set(m, { carryIn, budgeted: b, activity: a, available });
      }
    }
    const income = incomeByMonth.get(m) ?? 0;
    const raw = prevRaw - prevOverspent + income - monthBudgeted;
    months.set(m, {
      month: m,
      notBudgetedLastMonth: prevRaw,
      overspentLastMonth: prevOverspent,
      income,
      budgeted: monthBudgeted,
      budgetedInFuture: 0,
      availableToBudget: raw,
      raw,
      overspent,
      activity: monthActivity,
      available: monthAvailable,
    });
    prevRaw = raw;
    prevOverspent = overspent;
  }

  // ATB[m] = min(raw[m], max(0, raw[m] − S[m] + min_{k>m} S[k])), S[k] = Σ_{j≤k}(income[j] − budgeted[j]).
  const prefix: number[] = [];
  let running = 0;
  for (const m of order) {
    const s = months.get(m) as MonthSummary;
    running += s.income - s.budgeted;
    prefix.push(running);
  }
  let suffixMin = Infinity;
  for (let i = order.length - 1; i >= 0; i--) {
    const s = months.get(order[i] as IsoMonth) as MonthSummary;
    if (suffixMin !== Infinity) {
      const minFuture = s.raw - (prefix[i] as number) + suffixMin;
      s.availableToBudget = Math.min(s.raw, Math.max(0, minFuture));
      s.budgetedInFuture = s.raw - s.availableToBudget;
    }
    suffixMin = Math.min(suffixMin, prefix[i] as number);
  }

  return new BudgetResult(firstMonth, lastMonth, months, cells, modes);
}

function add(
  target: Map<BudgetRowKey, Map<IsoMonth, number>>,
  row: BudgetRowKey,
  month: IsoMonth,
  amount: number,
): void {
  let m = target.get(row);
  if (!m) {
    m = new Map();
    target.set(row, m);
  }
  m.set(month, (m.get(month) ?? 0) + amount);
}
